import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, ChevronLeft, ChevronRight, ArrowLeft, Crown, Shield, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleDateString() : "—");
const inputCls = "rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/60";

const Badge = ({ children, tone = "muted", testid }) => {
  const tones = { pro: "bg-[#F59E0B]/15 text-[#F59E0B]", admin: "bg-[#00F0FF]/15 text-[#00F0FF]", ok: "bg-emerald-500/15 text-emerald-400",
    no: "bg-red-500/15 text-red-400", muted: "bg-white/5 text-[#A1A1AA]", ov: "bg-[#E1306C]/15 text-[#FF7AA8]" };
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${tones[tone]}`}>{children}</span>;
};

function UserList({ onOpen }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [plan, setPlan] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.ucUsers({ q, role, plan, sort, page, limit: 25 }).then(setData).catch(() => toast.error("Could not load users"));
  }, [q, role, plan, sort, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const set = (fn) => (e) => { fn(e.target.value); setPage(1); };

  return (
    <div data-testid="uc-user-list">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
          <input value={q} onChange={set(setQ)} placeholder="Search name, email or user id" data-testid="uc-search" className={`${inputCls} w-full pl-8`} />
        </div>
        <select value={role} onChange={set(setRole)} data-testid="uc-filter-role" className={inputCls}>
          <option value="">All roles</option><option value="admin">Admins</option><option value="user">Users</option>
        </select>
        <select value={plan} onChange={set(setPlan)} data-testid="uc-filter-plan" className={inputCls}>
          <option value="">All plans</option><option value="pro">Pro</option><option value="free">Free</option>
        </select>
        <select value={sort} onChange={set(setSort)} data-testid="uc-sort" className={inputCls}>
          <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option><option value="email">Email A–Z</option><option value="last_active">Last active</option>
        </select>
      </div>
      {!data ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#00F0FF]" /></div> : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-widest text-[#A1A1AA]">
              <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Overrides</th><th className="px-4 py-3">Joined</th></tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.user_id} onClick={() => onOpen(u.user_id)} data-testid={`uc-row-${u.user_id}`} className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/[0.04]">
                  <td className="px-4 py-3 text-sm"><div className="font-semibold text-white">{u.name || "—"}</div><div className="text-xs text-[#A1A1AA]">{u.email}</div></td>
                  <td className="px-4 py-3">{u.role === "admin" ? <Badge tone="admin"><Shield size={11} />Admin</Badge> : <Badge>User</Badge>}</td>
                  <td className="px-4 py-3">{u.is_pro ? <Badge tone="pro"><Crown size={11} />Pro</Badge> : <Badge>Free</Badge>}</td>
                  <td className="px-4 py-3 text-sm text-[#A1A1AA]">{u.overrides || "—"}</td>
                  <td className="px-4 py-3 text-sm text-[#A1A1AA]">{fmt(u.created_at)}</td>
                </tr>
              ))}
              {!data.users.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#666]" data-testid="uc-empty">No users match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="mt-3 flex items-center justify-between text-xs text-[#A1A1AA]">
          <span data-testid="uc-total">{data.total} users</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="uc-prev" className="rounded-lg border border-white/10 p-1.5 disabled:opacity-30"><ChevronLeft size={14} /></button>
            <span data-testid="uc-page">Page {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="uc-next" className="rounded-lg border border-white/10 p-1.5 disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function AccessPanel({ d, onChange }) {
  const u = d.user;
  const [busy, setBusy] = useState("");
  const [expiry, setExpiry] = useState("");
  const run = async (key, body) => {
    setBusy(key);
    try { onChange(await api.ucUpdateAccess(u.user_id, body)); toast.success("Access updated — applies to the user immediately"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setBusy("");
  };
  const btn = (active) => `rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-40 ${active ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="uc-plan-panel">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Plan</div>
        <div className="mb-2 text-sm text-white">{d.is_pro ? "Pro" : "Free"} {u.subscription_status && <span className="text-xs text-[#666]">· subscription: {u.subscription_status}</span>}</div>
        {d.is_pro && u.pro_expires_at && <div className="mb-3 text-xs text-[#A1A1AA]">Pro until {fmt(u.pro_expires_at)} {u.pro_source === "admin" && "(admin grant)"}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => run("free", { plan: "free" })} disabled={!!busy} data-testid="uc-plan-free" className={btn(!d.is_pro)}>Free</button>
          <button onClick={() => run("pro", { plan: "pro", pro_expires_at: expiry || undefined })} disabled={!!busy} data-testid="uc-plan-pro" className={btn(d.is_pro)}>{busy === "pro" ? "Saving…" : "Give Pro"}</button>
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="uc-pro-expiry" className={`${inputCls} py-1 text-xs`} title="Optional Pro expiry (blank = permanent)" />
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="uc-role-panel">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Role</div>
        <div className="mb-3 text-sm text-white">{u.role === "admin" ? "Admin — full access to everything" : "Standard user"}</div>
        <div className="flex gap-2">
          <button onClick={() => run("user", { role: "user" })} disabled={!!busy} data-testid="uc-role-user" className={btn(u.role !== "admin")}>User</button>
          <button onClick={() => run("admin", { role: "admin" })} disabled={!!busy} data-testid="uc-role-admin" className={btn(u.role === "admin")}>Admin</button>
        </div>
      </div>
    </div>
  );
}

const REASON = { ok: "Included", off: "Feature off", maintenance: "Maintenance", forbidden: "Not rolled out", upgrade_required: "Pro only" };

function FeatureRow({ f, onSet, busy }) {
  const opts = [["default", "Default"], ["allow", "Add"], ["deny", "Remove"]];
  const cur = f.override || "default";
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-white/5 px-4 py-2.5" data-testid={`uc-feature-${f.feature_key}`}>
      <div className="min-w-[200px] flex-1">
        <div className="text-sm font-semibold text-white">{f.feature_name}</div>
        <div className="text-[11px] text-[#666]">{f.feature_key} · {f.plan.toUpperCase()} · {f.access_mode.replace("_", " ")} · default: {f.default_allow ? "has access" : REASON[f.default_reason]}</div>
      </div>
      {f.override && <Badge tone="ov">Manual</Badge>}
      <Badge tone={f.allow ? "ok" : "no"} testid={`uc-feature-status-${f.feature_key}`}>{f.allow ? "Has access" : "No access"}</Badge>
      <div className="flex overflow-hidden rounded-lg border border-white/10">
        {opts.map(([m, l]) => (
          <button key={m} disabled={busy || cur === m} onClick={() => onSet(f.feature_key, m)} data-testid={`uc-feature-${f.feature_key}-${m}`}
            className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${cur === m ? (m === "deny" ? "bg-red-500/80 text-white" : m === "allow" ? "bg-emerald-500/80 text-black" : "bg-white/15 text-white") : "text-[#A1A1AA] hover:bg-white/5"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function FeaturePanel({ d, setD }) {
  const [fq, setFq] = useState("");
  const [ff, setFf] = useState("all");
  const [busyKey, setBusyKey] = useState("");
  const list = useMemo(() => d.features.filter((f) => {
    const t = fq.toLowerCase();
    if (t && !f.feature_name.toLowerCase().includes(t) && !f.feature_key.includes(t)) return false;
    if (ff === "allowed") return f.allow;
    if (ff === "blocked") return !f.allow;
    if (ff === "manual") return !!f.override;
    if (ff === "pro") return f.plan === "pro";
    if (ff === "free") return f.plan === "free";
    return true;
  }), [d.features, fq, ff]);
  const onSet = async (key, mode) => {
    setBusyKey(key);
    try {
      const row = await api.ucSetOverride(d.user.user_id, key, mode);
      setD((p) => ({ ...p, features: p.features.map((x) => (x.feature_key === key ? row : x)) }));
      toast.success(mode === "default" ? "Reset to plan default" : mode === "allow" ? "Feature added for this user" : "Feature removed for this user");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setBusyKey("");
  };
  const allowed = d.features.filter((f) => f.allow).length;
  return (
    <div className="mt-5 rounded-xl border border-white/10" data-testid="uc-feature-panel">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <div className="mr-auto">
          <div className="text-sm font-bold text-white">Feature access</div>
          <div className="text-[11px] text-[#A1A1AA]" data-testid="uc-feature-summary">{allowed} of {d.features.length} features available · defaults follow their Free / Pro plan and Feature Control rollout</div>
        </div>
        <input value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Search features" data-testid="uc-feature-search" className={`${inputCls} py-1.5 text-xs`} />
        <select value={ff} onChange={(e) => setFf(e.target.value)} data-testid="uc-feature-filter" className={`${inputCls} py-1.5 text-xs`}>
          <option value="all">All</option><option value="allowed">Has access</option><option value="blocked">No access</option>
          <option value="manual">Manual overrides</option><option value="free">Free plan features</option><option value="pro">Pro plan features</option>
        </select>
      </div>
      <div className="max-h-[560px] overflow-y-auto">
        {list.map((f) => <FeatureRow key={f.feature_key} f={f} onSet={onSet} busy={busyKey === f.feature_key} />)}
        {!list.length && <div className="px-4 py-8 text-center text-xs text-[#666]">No features match.</div>}
      </div>
    </div>
  );
}

function StaffPanel({ d, reload }) {
  const [busy, setBusy] = useState("");
  const set = async (p, can_view, can_manage) => {
    setBusy(p.id);
    try {
      if (!can_view && !can_manage) await api.adminPageRevoke(p.id, d.user.user_id);
      else await api.adminPageGrant(p.id, { user_id: d.user.user_id, can_view, can_manage, can_publish: false });
      toast.success("Staff access updated"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setBusy("");
  };
  if (d.user.role === "admin") return <div className="mt-5 rounded-xl border border-white/10 p-4 text-xs text-[#A1A1AA]" data-testid="uc-staff-panel">Full admin — has access to every admin page.</div>;
  return (
    <div className="mt-5 rounded-xl border border-white/10" data-testid="uc-staff-panel">
      <div className="px-4 py-3"><div className="text-sm font-bold text-white">Staff access (admin pages)</div>
        <div className="text-[11px] text-[#A1A1AA]">Make this user a moderator or manager of specific admin pages without full admin. View = read-only, Manage = can approve / edit.</div></div>
      {(d.staff_pages || []).map((p) => {
        const lvl = p.can_manage ? "manage" : p.can_view ? "view" : "none";
        return (
          <div key={p.id} className="flex flex-wrap items-center gap-3 border-t border-white/5 px-4 py-2.5" data-testid={`uc-staff-${p.slug}`}>
            <div className="min-w-[200px] flex-1"><div className="text-sm font-semibold text-white">{p.name}</div><div className="text-[11px] text-[#666]">{p.description}</div></div>
            <div className="flex overflow-hidden rounded-lg border border-white/10">
              {[["none", "No access"], ["view", "View"], ["manage", "Manage"]].map(([k, l]) => (
                <button key={k} disabled={busy === p.id || lvl === k} onClick={() => set(p, k !== "none", k === "manage")} data-testid={`uc-staff-${p.slug}-${k}`}
                  className={`px-2.5 py-1 text-[11px] font-bold ${lvl === k ? (k === "none" ? "bg-white/15 text-white" : "bg-[#00F0FF] text-black") : "text-[#A1A1AA] hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UserDetail({ uid, onBack }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.ucUser(uid).then(setD).catch(() => toast.error("Could not load user")); }, [uid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const u = d.user;
  return (
    <div data-testid="uc-user-detail">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={onBack} data-testid="uc-back" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ArrowLeft size={13} /> All users</button>
        <button onClick={load} data-testid="uc-refresh" className="ml-auto flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        {u.picture ? <img src={u.picture} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#00F0FF]/15 font-display text-lg font-black text-[#00F0FF]">{(u.name || u.email || "?")[0].toUpperCase()}</div>}
        <div className="mr-auto">
          <div className="font-display text-lg font-black text-white" data-testid="uc-detail-name">{u.name || "—"}</div>
          <div className="text-xs text-[#A1A1AA]" data-testid="uc-detail-email">{u.email} · {u.user_id}</div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div><div className="text-[#666]">Joined</div><div className="text-white">{fmt(u.created_at)}</div></div>
          <div><div className="text-[#666]">Last active</div><div className="text-white">{fmt(u.last_active_at)}</div></div>
          <div><div className="text-[#666]">Projects</div><div className="text-white" data-testid="uc-detail-projects">{d.projects}</div></div>
        </div>
      </div>
      <AccessPanel d={d} onChange={setD} />
      <StaffPanel d={d} reload={load} />
      <FeaturePanel d={d} setD={setD} />
    </div>
  );
}

export default function UserControl() {
  const [uid, setUid] = useState(null);
  return (
    <div data-testid="user-control">
      <div className="mb-4">
        <h2 className="font-display text-lg font-black text-white">User Control</h2>
        <p className="text-xs text-[#A1A1AA]">Manage each user's plan, role and feature access. Changes apply to the user live.</p>
      </div>
      {uid ? <UserDetail uid={uid} onBack={() => setUid(null)} /> : <UserList onOpen={setUid} />}
    </div>
  );
}
